import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { Dispatch, SetStateAction } from 'react';
import type { Selection } from './orgNavTypes';

/**
 * Manages the state of the OrgNav component, uses URL search params to store the state.
 * Purpose is to allow usage of links to navigate to different companies, resellers, locations, etc.
 */

export type SelectionState = {
    type: Selection | null;
    id: string | number | null;
};

export const useOrgNavState = () => {
    const [searchParams, setSearchParams] = useSearchParams(new URLSearchParams());

    const state = useMemo(() => {
        const { type, id } = Object.fromEntries(searchParams);

        return {
            type: type || null,
            id: id ? (isNaN(Number(id)) ? id : parseInt(id)) : null,
        } as SelectionState;
    }, [searchParams]);

    const set: Dispatch<SetStateAction<SelectionState>> = (action) => {
        const next = typeof action === 'function' ? action(state) : action;

        // Merge — this hook only owns `type`/`id`; sibling params like `tab` must survive.
        setSearchParams((prev) => {
            const merged = new URLSearchParams(prev);
            merged.delete('type');
            merged.delete('id');
            if (next.type !== null) merged.set('type', next.type);
            if (next.id !== null) merged.set('id', next.id.toString());
            return merged;
        });
    };

    return [state, set] as const;
};
